import { createModel } from '@rematch/core';
import { post } from 'utils/http';
import {
  IPipelineProject,
  IProjectListResponse,
  ITaskListResponse,
  ICreateProjectParams,
  IGetProjectListParams,
  IUpdateProjectParams,
  IGetTaskListParams,
  IAllocateTasksParams,
  IProjectProgressDisplay,
  IGetFilterTaskDetailParams,
  IGetInspectTaskDetailParams,
  IFilterTaskDetailResponse,
  IInspectTaskDetailResponse,
  IBatchSubmitFilterResultParams,
  IBatchSubmitInspectResultParams,
  IUpdateSampleRatioParams,
  IUpdateSampleRatioResponse,
  IBatchUpdateFilterResultParams,
  IBatchUpdateInspectResultParams,
  // 标注相关类型
  IGetAnnotationTaskDetailParams,
  ISaveAnnotationResultParams,
  ISubmitAnnotationTaskParams,
  IGetAnnotationInspectTaskDetailParams,
  IAnnotationInspectTaskDetailResponse,
  ISaveAnnotationInspectResultParams,
  ISubmitAnnotationInspectTaskParams,
  // 质检备注相关类型
  IUpdateTaskInspectRemarkParams,
  // 图片编辑相关类型
  ISaveEditedCorpusImageParams,
  ISaveEditedCorpusImageResponse,
  IGetStorageHistoryReq,
  IGetStorageHistoryRes,
} from 'types/pipelineProject';

export default createModel()({
  state: {
    projectList: [],
    currentProject: null,
    taskList: [],
    corpusSetList: [],
  },
  reducers: {
    setState: (state: any, payload: any) => ({ ...state, ...payload }),
  },
  effects: (dispatch) => ({
    async createProject(params: ICreateProjectParams) {
      const res = await post('/api/pipelineProject/createProject', params);
      return res;
    },

    async getProjectList(params: IGetProjectListParams) {
      const res = await post('/api/pipelineProject/getProjectList', params);
      if (res) {
        const data: IProjectListResponse = res;
        this.setState({
          projectList: data.list || [],
        });
        return {
          projectList: data.list || [],
          page: {
            totalItemNum: data.total || 0,
            pageNum: params.pageNum,
            pageSize: params.pageSize,
          },
        };
      }
      return null;
    },

    async getProjectDetail(params: { projectId: number }) {
      const res = await post('/api/pipelineProject/getProjectDetail', params);
      if (res) {
        const project: IPipelineProject = res;
        this.setState({
          currentProject: project,
        });
        return project;
      }
      return null;
    },

    async updateProject(params: IUpdateProjectParams) {
      const res = await post('/api/pipelineProject/updateProject', params);
      return res;
    },

    async deleteProject(params: { id: number }) {
      const res = await post('/api/pipelineProject/deleteProject', params);
      return res;
    },

    async getTaskList(params: IGetTaskListParams) {
      const res = await post('/api/pipelineProject/getTaskList', params);
      if (res) {
        const data: ITaskListResponse = res;
        this.setState({
          taskList: data.list || [],
        });
        return {
          taskList: data.list || [],
          page: {
            totalItemNum: data.total || 0,
            pageNum: params.pageNum,
            pageSize: params.pageSize,
          },
        };
      }
      return null;
    },

    async allocateTasks(params: IAllocateTasksParams) {
      const res = await post('/api/pipelineProject/allocateTasks', params);
      return res;
    },

    async collectRemainingTasks(params: { taskId: number }) {
      const res = await post('/api/pipelineProject/collectRemainingTasks', params);
      return res;
    },

    async claimInspectTask(params: { taskId: number }) {
      const res = await post('/api/pipelineProject/claimInspectTask', params);
      return res;
    },

    async exportOperatorStats(params: { projectId: number }) {
      const res = await post('/api/pipelineProject/exportOperatorStats', params);
      return res;
    },

    async getProjectProgress(params: { projectId: number }): Promise<IProjectProgressDisplay> {
      const project = await dispatch.pipelineProject.getProjectDetail(params);
      if (project?.progress) {
        const { progress } = project;
        return {
          operateProgress: progress.operateRate,
          operateCompleted: progress.operateCompleted,
          operateTotal: progress.operateTotal,
          inspectProgress: progress.inspectRate,
          inspectCompleted: progress.inspectCompleted,
          inspectTotal: progress.inspectTotal,
          inspectCompletedStored: progress.inspectCompletedStored ?? 0,
          inspectCompletedStoredRate: progress.inspectCompletedStoredRate ?? 0,
          inspectCompletedNotStored: progress.inspectCompletedNotStored ?? 0,
          inspectCompletedNotStoredRate: progress.inspectCompletedNotStoredRate ?? 0,
        };
      }
      return {
        operateProgress: 0,
        operateCompleted: 0,
        operateTotal: 0,
        inspectProgress: 0,
        inspectCompleted: 0,
        inspectTotal: 0,
        inspectCompletedStored: 0,
        inspectCompletedStoredRate: 0,
        inspectCompletedNotStored: 0,
        inspectCompletedNotStoredRate: 0,
      };
    },

    async storageCorpus(params: {
      projectId: number;
      corpusSetId: number;
      newCorpusSetId?: string;
      newCorpusSetName?: string;
      newCorpusSetDesc?: string;
      projectType: number;
      machineLabelCorpusSetId?: number;
    }) {
      const res = await post('/api/pipelineProject/storageCorpus', params);
      return res;
    },

    async getCorpusSetList(data = {}) {
      const rst = await post('/api/pipeline/getCorpusSetList', data);
      if (rst?.corpusSetList) {
        this.setState({
          corpusSetList: rst.corpusSetList || [],
        });
        return {
          corpusSetList: rst.corpusSetList || [],
          page: rst.page || { totalPageNum: 0, pageNum: 1, pageSize: 10 },
        };
      }
      return {
        corpusSetList: [],
        page: { totalPageNum: 0, pageNum: 1, pageSize: 10 },
      };
    },

    async getProjectCorpusStats(params: { projectId: number }) {
      const res = await post('/api/pipelineProject/getProjectCorpusStats', params);
      if (res) {
        return {
          totalCorpusCount: res.totalCorpusCount || 0,
          pendingCorpusCount: res.pendingCorpusCount || 0,
        };
      }
      return {
        totalCorpusCount: 0,
        pendingCorpusCount: 0,
      };
    },

    async getFilterTaskDetail(params: IGetFilterTaskDetailParams): Promise<IFilterTaskDetailResponse | null> {
      const res = await post('/api/pipelineProject/getFilterTaskDetail', params);
      if (res) {
        return {
          taskId: res.taskId,
          total: res.total || 0,
          completedCount: res.completedCount || 0,
          keptCount: res.keptCount || 0,
          list: res.list || [],
        };
      }
      return null;
    },

    async getInspectTaskDetail(params: IGetInspectTaskDetailParams): Promise<IInspectTaskDetailResponse | null> {
      const res = await post('/api/pipelineProject/getInspectTaskDetail', params);
      if (res) {
        return {
          taskId: res.taskId,
          taskTotal: res.taskTotal || 0,
          total: res.total || 0,
          completedCount: res.completedCount || 0,
          keptCount: res.keptCount || 0,
          sampleRatio: res.sampleRatio || 0,
          sampleTotal: res.sampleTotal || 0,
          list: res.list || [],
        };
      }
      return null;
    },

    async increaseSampleRatio(params: IUpdateSampleRatioParams): Promise<IUpdateSampleRatioResponse | null> {
      const res = await post('/api/pipelineProject/increaseSampleRatio', params);
      if (res) {
        return {
          code: res.code || 0,
          sampledCount: res.sampledCount || 0,
        };
      }
      return null;
    },

    async batchSubmitFilterResult(params: IBatchSubmitFilterResultParams) {
      const res = await post('/api/pipelineProject/batchSubmitFilterResult', params);
      return res;
    },

    async batchSubmitInspectResult(params: IBatchSubmitInspectResultParams) {
      const res = await post('/api/pipelineProject/batchSubmitInspectResult', params);
      return res;
    },

    async batchUpdateFilterResult(params: IBatchUpdateFilterResultParams) {
      const res = await post('/api/pipelineProject/batchUpdateFilterResult', params);
      return res;
    },

    async batchUpdateInspectResult(params: IBatchUpdateInspectResultParams) {
      const res = await post('/api/pipelineProject/batchUpdateInspectResult', params);
      return res;
    },

    async getAnnotationTaskDetail(
      params: IGetAnnotationTaskDetailParams,
    ): Promise<IAnnotationInspectTaskDetailResponse | null> {
      const res = await post('/api/pipelineProject/getAnnotationTaskDetail', params);
      if (res) {
        return {
          taskId: res.taskId,
          projectId: res.projectId,
          total: res.total || 0,
          completedCount: res.completedCount || 0,
          annotationTemplate: res.annotationTemplate || { fields: [] },
          list: res.list || [],
        };
      }
      return null;
    },

    async saveAnnotationResult(params: ISaveAnnotationResultParams) {
      const res = await post('/api/pipelineProject/saveAnnotationResult', params);
      return res;
    },

    async submitAnnotationTask(params: ISubmitAnnotationTaskParams) {
      const res = await post('/api/pipelineProject/submitAnnotationTask', params);
      return res;
    },

    async getAnnotationInspectTaskDetail(
      params: IGetAnnotationInspectTaskDetailParams,
    ): Promise<IAnnotationInspectTaskDetailResponse | null> {
      const res = await post('/api/pipelineProject/getAnnotationInspectTaskDetail', params);
      if (res) {
        return {
          taskId: res.taskId,
          projectId: res.projectId,
          taskTotal: res.taskTotal || 0,
          sampleTotal: res.sampleTotal || 0,
          total: res.total || 0,
          completedCount: res.completedCount || 0,
          sampleRatio: res.sampleRatio || 0,
          annotationTemplate: res.annotationTemplate || { fields: [] },
          list: res.list || [],
        };
      }
      return null;
    },

    async saveAnnotationInspectResult(params: ISaveAnnotationInspectResultParams) {
      const res = await post('/api/pipelineProject/saveAnnotationInspectResult', params);
      return res;
    },

    async submitAnnotationInspectTask(params: ISubmitAnnotationInspectTaskParams) {
      const res = await post('/api/pipelineProject/submitAnnotationInspectTask', params);
      return res;
    },

    // 更新任务质检备注
    async updateTaskInspectRemark(params: IUpdateTaskInspectRemarkParams) {
      const res = await post('/api/pipelineProject/updateTaskInspectRemark', params);
      return res;
    },

    // 获取入库历史
    async getStorageHistory(params: IGetStorageHistoryReq): Promise<IGetStorageHistoryRes | null> {
      const res = await post('/api/pipelineProject/getStorageHistory', params);
      return res;
    },

    // 保存编辑后的语料图片
    async saveEditedCorpusImage(params: ISaveEditedCorpusImageParams): Promise<ISaveEditedCorpusImageResponse | null> {
      const formData = new FormData();
      formData.append('file', params.imageFile);
      formData.append('taskId', String(params.taskId));
      formData.append('itemId', params.itemId);

      const res = await post('/api/pipelineProject/saveEditedCorpusImage', {}, { formData });
      if (res) {
        return {
          code: res.code || 0,
          newCorpusUrl: res.newCorpusUrl,
          message: res.message,
        };
      }
      return null;
    },
  }),
});
